import React, { useContext, useEffect, useRef } from 'react';
import { func } from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

// Utils
import { calculateSuccessfulResults } from 'feature/loading-management/utils/calculate';

import Buttons from '../../components/Buttons';
import SequencingProgress from '../../components/SequencingProgress';
import SubmitProgressHeader from '../../components/SubmitProgressHeader';
import { SequencingWizardContext } from '../../SequencingWizardContext';

const LastStep6 = ({ editBatchApplySequence, close }) => {
  const { viewBy } = useParams();
  const {
    batchUpdating,
    data: {
      changeList,
      currentAffiliate,
      currentSelection,
      futureDate,
      inventoryOwners,
      locations,
      products,
    },
    initialData,
    progress,
    setCurrentStep,
    setData,
    setProgress,
  } = useContext(SequencingWizardContext);
  const { dataCompanyId, customerIdName } = currentAffiliate;
  const isMounted = useRef(true);

  const handleStartAnother = () => {
    setData(initialData);
    setCurrentStep(0);
  };

  useEffect(() => {
    return () => setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = {
      action: 'Append',
      companyCustomerIdName: customerIdName,
      currentSelection,
      dataCompanyId,
      futureSequenceDate: futureDate,
      inventoryOwners,
      locations,
      products,
      viewBy,
    };
    editBatchApplySequence(params).then(result => {
      isMounted.current &&
        setData(prevData => ({
          ...prevData,
          changeList: result,
        }));
      isMounted.current && setProgress(100);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastInventoryOwner = `${
    inventoryOwners[inventoryOwners.length - 1]?.companyCustomerIdName
  } ${inventoryOwners[inventoryOwners.length - 1]?.nameLong}`;
  const successfulResults = calculateSuccessfulResults(changeList);

  return (
    <>
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          display: 'flex',
          flexDirection: 'column',
          // height of other components is 157px
          // prevents progress bar from looking off
          height: 'calc(100% - 157px)',
          overflowY: 'auto',
          pt: 3,
        }}
      >
        <SubmitProgressHeader totalProgress={progress} />
        <Typography
          sx={{ marginBottom: 2, marginTop: 4.5, padding: '0 16px', textAlign: 'center' }}
          variant="body1"
        >
          {batchUpdating
            ? `Setting ${lastInventoryOwner}`
            : `${lastInventoryOwner} successfully set`}
          to the last sequence position for the below products and locations.
        </Typography>
        {!batchUpdating && (
          <Typography
            sx={{ marginBottom: 2, padding: '0 16px', textAlign: 'center' }}
            variant="body1"
          >
            A total of{' '}
            {successfulResults === 1
              ? `${successfulResults} change was`
              : `${successfulResults} changes were`}{' '}
            made. This change can take up to 15 minutes to appear in the application.
          </Typography>
        )}
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'graphic.lighter',
            borderBottom: '0.1em solid',
            borderBottomColor: 'graphic.lighter',
            borderTop: '0.1em solid',
            borderTopColor: 'graphic.lighter',
            display: 'flex',
            minHeight: 48,
            px: 3,
            py: 0,
            width: '100%',
            zIndex: 4,
          }}
        >
          <Typography sx={{ color: 'primary.dark' }} variant="subtitle1">
            Summary
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: 'graphic.light', pb: 0.5, pt: 1, px: 3 }}>
          <Typography variant="subtitle1">Selected Inventory Owner</Typography>
        </Box>
        <Box bgcolor="graphic.lighter" px={3} py={2}>
          <Typography variant="body1">{lastInventoryOwner}</Typography>
        </Box>
        <Box sx={{ backgroundColor: 'graphic.light', pb: 0.5, pt: 1, px: 3 }}>
          <Typography sx={{ color: 'text.secondary' }} variant="subtitle1">
            Updating Sequences
          </Typography>
        </Box>
        <Box bgcolor="graphic.lighter" height="100%">
          <SequencingProgress />
        </Box>
      </Box>
      <Buttons
        handleLeftButtonClick={handleStartAnother}
        handleRightButtonClick={close}
        leftLabel="Start Another"
        loadingData={batchUpdating}
        rightLabel="Close"
      />
    </>
  );
};

LastStep6.propTypes = {
  close: func.isRequired,
  editBatchApplySequence: func.isRequired,
};

export default LastStep6;
